import {
  Card,
  CardActionArea,
  CardContent,
  Divider,
  Grid,
  Typography,
} from '@mui/material';
import { skipToken } from '@reduxjs/toolkit/query';
import { useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useUser } from '../../Hooks/useUser';
import { useGetClientQuery } from '../../Redux/api/clientApi';
import { useGetTalentQuery } from '../../Redux/api/talentApi';
import { setRead } from '../../Redux/slices/NotificationSlice';
import { useAppDispatch, useAppSelector } from '../../Redux/store';
import { ProfileType } from '../../Types/General';
import { INotification, NotificationType } from '../../Types/Notification';
import { formatTimestamp } from '../../Utils/String';
import { KaziAvatar } from '../Kazi/KaziAvatar';

interface NotificationCardProps {
  notification: INotification;
  showDivider?: boolean;
}
export const NotificationCard = ({
  notification,
  showDivider = false,
}: NotificationCardProps) => {
  /* ******************** Hooks ******************** */
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const { type } = useUser();
  const isMessage = notification.type === NotificationType.Message;
  const isClient = type == ProfileType.Client;
  const { data: Talent } = useGetTalentQuery(
    (isMessage && isClient && notification.navigation_locator) || skipToken,
  );
  const { data: Client } = useGetClientQuery(
    (isMessage && !isClient && notification.navigation_locator) || skipToken,
  );
  /* ******************** Variables ******************** */
  const readNotifications = useAppSelector(state => state.Notification.read);
  const isRead = readNotifications.some(n => n === notification.locator);
  const notificationUser = useMemo(() => {
    return !isClient ? Client : Talent;
  }, [Talent, Client]);
  /* ******************** Functions ******************** */
  const handleClick = () => {
    const locator = notification.navigation_locator;
    const newReadNotifications = readNotifications.concat([
      notification.locator,
    ]);
    dispatch(setRead(newReadNotifications));
    switch (notification.type) {
      case NotificationType.Message:
        navigate(`/messages?conversation=${locator}`);
        break;
      case NotificationType.JobApplication:
        navigate(`/job-postings/applications/${locator}`);
        break;
      case NotificationType.UpcomingPayment:
        break;
      case NotificationType.ApplicationAccepted:
        navigate(`/marketplace/posting/${locator}`);
        break;
      case NotificationType.ApplicationInvite:
      case NotificationType.ApplicationDeclined:
        navigate(`/job-postings/posting/${locator}`);
        break;
      case NotificationType.JobReady:
      case NotificationType.JobComplete:
        navigate(`/active-jobs`);
        break;
      case NotificationType.NewReview:
        isClient ? navigate('profile/client') : navigate('profile/talent');
        break;
    }
  };
  /* ******************** Effects ******************** */
  useEffect(() => {
    return () => {
      if (readNotifications.length > 0) {
        // deleteNotifications({
        //     locator: user?.locator!,
        //     body: readNotifications
        // })
      }
    };
  }, [readNotifications]);
  /* ******************** JSX ******************** */
  return (
    <>
      <Card>
        <CardActionArea onClick={handleClick}>
          <CardContent
            sx={{
              display: 'flex',
              flexDirection: 'row',
              paddingX: '16px',
              paddingY: '12px',
              gap: '16px',
            }}
          >
            <KaziAvatar
              width="32px"
              sx={{ alignSelf: 'center' }}
              locator={notificationUser?.locator}
              first_name={notificationUser?.first_name}
              last_name={notificationUser?.last_name}
            />
            <Grid
              display={'flex'}
              flexDirection={'column'}
              fontWeight={isRead ? '' : 'bolder'}
            >
              <Typography variant="body-small">
                {notification.message}
              </Typography>
              <Typography variant="body-smaller" color={'black.200'}>
                {formatTimestamp(
                  (
                    notification.createdAt || notification.created_at
                  )?.toString(),
                )}
              </Typography>
            </Grid>
          </CardContent>
        </CardActionArea>
      </Card>
      {showDivider && <Divider />}
    </>
  );
};
